import {
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSFlexBox,
  WSForm,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useLayoutEffect, useMemo, useState } from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { ErrorContextKey } from "../../../services/platform";
import { CardSuccessPhysical, vgVaultKey } from "../BankCard/BankCard";
import { useCardQuery } from "../../../query/payments/queries";
import { useWSStore } from "../../../store";
import styles from "./CardList.module.scss";
import { ADD_PIN_CARD_MODAL } from "./AddPin";
import { useUpdateCard } from "../../../query/payments/mutations";

export const ACTIVATE_CARD_MODAL = "ACTIVATE_CARD_MODAL";

const Inner: React.FC<{ cardId: string }> = props => {
  const { closeModal, openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();

  const cardQuery = useCardQuery(props.cardId);
  const [updateCard, updateCardMeta] = useUpdateCard();

  const [loading, setLoading] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [formState, setFormState] = useState<any>(null);

  const validationError = !!formState?.["data.attributes.pin"]?.errorMessages
    ?.length;

  const {
    bankCardTokens: { token, ids }
  } = useWSStore();

  const cardID = ids[props.cardId];

  const form = useMemo(() => {
    return window.VGSCollect.create(
      vgVaultKey,
      process.env.REACT_APP_ENV === "production" ? "live" : "sandbox",
      setFormState
    );
  }, []);

  useLayoutEffect(() => {
    if (window.document.querySelector("#cc-pin iframe")) {
      return;
    }

    form.field("#cc-cvv2", {
      type: "card-security-code",
      name: "data.attributes.cvv2",
      successColor: "#4F8A10",
      errorColor: "#D8000C",
      placeholder: "123",
      maxLength: 3,
      validations: ["required", "validCardSecurityCode"]
    });

    form.field("#cc-expiration-date", {
      type: "card-expiration-date",
      name: "data.attributes.expirationDate",
      successColor: "#4F8A10",
      errorColor: "#D8000C",
      placeholder: "MM / YY",
      validations: ["required", "validCardExpirationDate"],
      // Change to required experiation date format
      serializers: [
        form.SERIALIZERS.replace("(\\d{2})\\s\\/\\s(\\d{2})", "$2-$1")
      ]
    });
  }, []);

  return (
    <>
      <WSText.Heading2 mb="M">Activate physical card</WSText.Heading2>

      <WSFlexBox.Center>
        <CardSuccessPhysical />
      </WSFlexBox.Center>

      <WSForm
        id="cc-form"
        onSubmit={() => {
          if (validationError) {
            return;
          }

          setLoading(true);
          form.submit(
            `/cards/${cardID}/activate`,
            {
              mapDotToObject: "true",
              headers: {
                "Content-Type": "application/vnd.api+json",
                Authorization: "Bearer " + token
              }
            },
            async (status: any, other: any) => {
              if (status === 200) {
                setLoading(false);
                closeModal(ACTIVATE_CARD_MODAL);
                openSnackbar({
                  type: "success",
                  message: "Card successfully activated!"
                });
                openModal(ADD_PIN_CARD_MODAL, { cardId: props.cardId });
              } else {
                setError(other?.errors[0]?.title || "Something went wrong");
                setLoading(false);
              }
            }
          );
        }}
      >
        <WSForm.Label mt="M">Expiration Date</WSForm.Label>
        <span id="cc-expiration-date" className={styles.input}></span>

        <WSForm.Label mt="M">CVV2</WSForm.Label>
        <span id="cc-cvv2" className={styles.input}></span>

        <WSErrorMessage
          mt="M"
          error={error}
          contextKey={ErrorContextKey.Other}
        />

        <WSButton.Primary
          disabled={validationError}
          loading={loading || updateCardMeta.isLoading}
          fullWidth
          mt="M"
          type="submit"
        >
          Confirm
        </WSButton.Primary>
      </WSForm>
    </>
  );
};

export const ActivateCardModal: React.FC = () => {
  const { closeModal } = useModalContext();
  const onClose = () => closeModal(ACTIVATE_CARD_MODAL);

  return (
    <>
      <WSModal name={ACTIVATE_CARD_MODAL} size="S" onClose={onClose}>
        {({ cardId }) => <Inner cardId={cardId}></Inner>}
      </WSModal>
    </>
  );
};
